import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useLiveClasses } from '../../hooks/useLiveClasses';
import { liveClassLink } from '../../helpers/liveClasses';

/**
 * /live-stream is the trainer's page and only draws anything for an admin.
 * Anyone else who opens it (a member handed the trainer's link) goes to the
 * class that's live instead of a blank white page. With no class on, a
 * signed-in member goes to AF Studio and a signed-out visitor to the login
 * page, as before (so the trainer can still sign in from here).
 */

function Loading() {
  return (
    <div
      role='status'
      aria-label='Loading'
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#080B14',
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: '4px solid rgba(255,255,255,0.15)',
          borderTopColor: '#ffffff',
          animation: 'spin 0.8s linear infinite',
        }}
      />
    </div>
  );
}

function SendToLiveClass({ isAuthenticated }) {
  const { liveClasses, isLoading } = useLiveClasses();
  if (isLoading) return <Loading />;
  const link = liveClassLink(liveClasses, { isAuthenticated });
  if (link) return <Navigate to={link.href} replace />;
  if (!isAuthenticated) {
    return <Navigate to='/login' state={{ from: '/live-stream' }} replace />;
  }
  return <Navigate to='/dashboard/videos/all' replace />;
}

export default function TrainerGate({ children }) {
  const { isAuthenticated, userLoading, user } = useSelector((state) => state.auth);
  // Wait for sign-in to settle: a trainer reloading mid-class must not be
  // sent to the members' page before we know they're the admin.
  if (userLoading) return <Loading />;
  if (isAuthenticated && user?.role?.includes('admin')) return children;
  return <SendToLiveClass isAuthenticated={!!isAuthenticated} />;
}
